import React, { useState } from 'react';
import { TrainingData } from './TrainingWizard';
import { CalendarIcon, ClockIcon, MapPinIcon } from 'lucide-react';
// Mock data for locations
const mockLocations = [{
  id: 1,
  name: 'Rotterdam',
  address: 'Havenstraat 123, Rotterdam',
  availability: [1, 2, 3, 4, 5]
}, {
  id: 2,
  name: 'Utrecht',
  address: 'Stationsweg 45, Utrecht',
  availability: [1, 3, 5]
}, {
  id: 3,
  name: 'Amsterdam',
  address: 'Industrieweg 78, Amsterdam',
  availability: [2, 4]
}, {
  id: 4,
  name: 'Eindhoven',
  address: 'Techniekplein 12, Eindhoven',
  availability: [1, 2, 3, 4, 5]
}];
interface LocationDateTimeStepProps {
  trainingData: TrainingData;
  updateTrainingData: (data: Partial<TrainingData>) => void;
}
export function LocationDateTimeStep({
  trainingData,
  updateTrainingData
}: LocationDateTimeStepProps) {
  const [selectedDate, setSelectedDate] = useState<string>(trainingData.startDate ? new Date(trainingData.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState<string>(trainingData.startDate ? new Date(trainingData.startDate).toTimeString().slice(0, 5) : '09:00');
  const [endTime, setEndTime] = useState<string>(trainingData.endDate ? new Date(trainingData.endDate).toTimeString().slice(0, 5) : '12:00');
  const handleLocationSelect = (locationId: number) => {
    const selectedLocation = mockLocations.find(location => location.id === locationId);
    if (selectedLocation) {
      updateTrainingData({
        locationId,
        locationName: selectedLocation.name
      });
    }
  };
  const handleDateTimeChange = () => {
    const startDate = new Date(`${selectedDate}T${startTime}:00`);
    const endDate = new Date(`${selectedDate}T${endTime}:00`);
    updateTrainingData({
      startDate,
      endDate
    });
  };
  return <div data-id="element-3225">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3226">
        Selecteer Locatie & Tijdstip
      </h2>
      <div className="space-y-6" data-id="element-3227">
        {/* Location selection */}
        <div data-id="element-3228">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-3229">
            <MapPinIcon className="h-4 w-4 mr-1" data-id="element-3230" />
            Locatie
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3231">
            {mockLocations.map(location => <div key={location.id} className={`p-4 border rounded-lg cursor-pointer transition ${trainingData.locationId === location.id ? 'border-2 border-[#B61C1C] bg-red-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} onClick={() => handleLocationSelect(location.id)} data-id="element-3232">
                <h4 className="font-medium text-gray-900" data-id="element-3233">{location.name}</h4>
                <p className="text-sm text-gray-600 mt-1" data-id="element-3234">{location.address}</p>
                <div className="mt-2 flex flex-wrap gap-1" data-id="element-3235">
                  {location.availability.map(day => <span key={day} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3236">
                      {day === 1 && 'Ma'}
                      {day === 2 && 'Di'}
                      {day === 3 && 'Wo'}
                      {day === 4 && 'Do'}
                      {day === 5 && 'Vr'}
                    </span>)}
                </div>
              </div>)}
          </div>
        </div>
        {/* Date selection */}
        <div data-id="element-3237">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-3238">
            <CalendarIcon className="h-4 w-4 mr-1" data-id="element-3239" />
            Datum
          </h3>
          <input type="date" className="w-full p-2 border border-gray-300 rounded-md" value={selectedDate} onChange={e => {
          setSelectedDate(e.target.value);
          handleDateTimeChange();
        }} min={new Date().toISOString().split('T')[0]} data-id="element-3240" />
        </div>
        {/* Time selection */}
        <div data-id="element-3241">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-3242">
            <ClockIcon className="h-4 w-4 mr-1" data-id="element-3243" />
            Tijdstip
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3244">
            <div data-id="element-3245">
              <label className="block text-xs text-gray-500 mb-1" data-id="element-3246">
                Starttijd
              </label>
              <input type="time" className="w-full p-2 border border-gray-300 rounded-md" value={startTime} onChange={e => {
              setStartTime(e.target.value);
              handleDateTimeChange();
            }} data-id="element-3247" />
            </div>
            <div data-id="element-3248">
              <label className="block text-xs text-gray-500 mb-1" data-id="element-3249">
                Eindtijd
              </label>
              <input type="time" className="w-full p-2 border border-gray-300 rounded-md" value={endTime} onChange={e => {
              setEndTime(e.target.value);
              handleDateTimeChange();
            }} data-id="element-3250" />
            </div>
          </div>
        </div>
        {/* Summary */}
        {trainingData.locationId && trainingData.startDate && trainingData.endDate && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-3251">
              <h3 className="font-medium text-gray-900 mb-2" data-id="element-3252">Samenvatting</h3>
              <div className="space-y-2 text-sm" data-id="element-3253">
                <div className="flex items-start" data-id="element-3254">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mr-2 mt-0.5" data-id="element-3255" />
                  <div data-id="element-3256">
                    <p className="text-gray-900 font-medium" data-id="element-3257">
                      {mockLocations.find(loc => loc.id === trainingData.locationId)?.name}
                    </p>
                    <p className="text-gray-600" data-id="element-3258">
                      {mockLocations.find(loc => loc.id === trainingData.locationId)?.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-3259">
                  <CalendarIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-3260" />
                  <p className="text-gray-900" data-id="element-3261">
                    {new Date(trainingData.startDate).toLocaleDateString('nl-NL', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
                  </p>
                </div>
                <div className="flex items-center" data-id="element-3262">
                  <ClockIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-3263" />
                  <p className="text-gray-900" data-id="element-3264">
                    {new Date(trainingData.startDate).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                    -{' '}
                    {new Date(trainingData.endDate).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </p>
                </div>
              </div>
            </div>}
      </div>
    </div>;
}